import { Modal } from '@asgard/ui-library';
import { faWarning } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useI18n } from 'hooks/useI18n';
import dynamic from 'next/dynamic';
import { useEffect, useState } from 'react';
import { StyledIcon, StyledText, StyledTitle } from './ConfirmModal.styled';
import { ConfirmModalProps } from './ConfirmModal.types';

export const ConfirmModal = <ItemId,>({
  isOpen,
  close,
  title,
  text,
  action,
  actionId,
  confirmText,
  cancelText,
  closeAfterConfirm = true,
  icon = faWarning,
  iconVariant = 'warning'
}: ConfirmModalProps<ItemId>): JSX.Element => {
  const { t } = useI18n('common');
  const [submitting, setSubmitting] = useState(false);

  const handleConfirmClick = async (): Promise<void> => {
    setSubmitting(true);

    try {
      await action(actionId || 1);

      if (closeAfterConfirm) {
        setSubmitting(false);
        close();
      }
    } catch (ex) {
      setSubmitting(false);
      throw ex;
    }
  };

  useEffect(() => {
    if (isOpen) {
      setSubmitting(false);
    }
  }, [isOpen]);

  return (
    <Modal
      buttonsFullWidth
      closeable={false}
      isOpen={isOpen}
      title=""
      buttonCancel={{
        onClick: close,
        label: cancelText || t('confirmModal.actions.cancel')
      }}
      buttonSuccess={{
        onClick: handleConfirmClick,
        isLoading: submitting,
        label: confirmText || t('confirmModal.actions.confirm')
      }}
    >
      <StyledIcon variant={iconVariant}>
        <FontAwesomeIcon icon={icon} />
      </StyledIcon>
      <StyledTitle>{title || t('confirmModal.title')}</StyledTitle>
      <StyledText>{text || t('confirmModal.text')}</StyledText>
    </Modal>
  );
};

export default ConfirmModal;

export const DynamicConfirmModal = dynamic(
  () => import('components/modals/ConfirmModal'),
  {
    ssr: false
  }
) as typeof ConfirmModal;
